import { Elements, ExpressCheckoutElement, useElements } from '@stripe/react-stripe-js';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import { PaymentMethodEnum, ProviderEnum } from '@/types/common';
import type {
  AvailablePaymentMethods,
  Stripe,
  StripeExpressCheckoutElementConfirmEvent,
  StripeExpressCheckoutElementReadyEvent,
  StripeExpressCheckoutElementOptions,
} from '@stripe/stripe-js';
import { Loader } from '../Common/Loader';
import { APPLE_PAY_WALLET, GOOGLE_PAY_WALLET } from '@/constants/stripe';
import { useStripeInitialize } from '@/hooks/useStripeInitialize';
import { useExpressWalletFallback } from '@/hooks/useExpressWalletFallback';
import {
  EXPRESS_WALLET_ECE_HANG_TIMEOUT_MS,
  EXPRESS_WALLET_ECE_HANG_TIMEOUT_REASON,
  useExpressReadyTimeout,
} from '@/hooks/useExpressReadyTimeout';
import { useStripeWalletShippingZipTax } from '@/hooks/useStripeWalletShippingZipTax';
import {
  useStableStripeExpressElementsOptions,
  useSyncStripeElementsAmount,
} from '@/hooks/useStableStripeExpressElements';
import { logError } from '@/utils/logger';
import { ErrorLogs } from '@/constants/logs';
import { getLogInfoData } from '@/utils/logs';
import {
  getStripeExpressAvailableMethods,
  failExpressCheckoutSheet,
  getStripeExpressCheckoutBusinessConfig,
  getStripeExpressCheckoutShippingConfig,
  isExpressPaymentFailure,
  isStripeExpressPaymentMethodExists,
} from '@/utils/stripe';
import { getDeviceType } from '@/utils/device';
import { expressMethodForDevice, shouldRestrictExpressToNativeWallet } from '@/utils/deviceExpressSupport';
import type { ExpressCheckoutComponentProps } from '@/utils/expressPayment';
import { notifyPaymentIntentClick, trackAndNotifyPaymentClick } from '@/utils/paymentClickEvents';
import {
  trackApplePaySlowLoad,
  trackExpressWalletAvailability,
  type ApplePayLoadPhases,
  type ExpressWalletName,
} from '@/utils/conversionTracking';
import type { WalletReadyState } from '@/components/Wallets/WalletButton/WalletButtonShell';
import { ApplePayButtonPlaceholder } from '@/components/Wallets/WalletButton/ApplePayButtonPlaceholder';
import { GooglePayButtonPlaceholder } from '@/components/Wallets/WalletButton/GooglePayButtonPlaceholder';
import { ApplePayTabFallback } from '@/components/Wallets/ApplePayTabFallback/ApplePayTabFallback';
import { useApplePayTabFallbackGate } from '@/hooks/useApplePayTabFallbackGate';
import { armApplePayTabFallbackIfEligible, markApplePayTabUnusable } from '@/utils/applePayTabFallback';
import { WalletSlot } from '@/components/Wallets/WalletButton/WalletSlot';
import { getWalletPairClass } from '@/components/Wallets/WalletButton/walletPairClass';
import { resolveCheckoutGameName } from '@/utils/publisher';
import '@/components/Wallets/WalletButton/walletButton.css';

interface ExpressCheckoutButtonProps extends ExpressCheckoutComponentProps {
  /** The instance behind the enclosing `<Elements>` — the payment must run on it. */
  stripe: Stripe;
  onElementReady: (e: StripeExpressCheckoutElementReadyEvent) => void;
  onElementError: () => void;
  allowApplePay: boolean;
  allowGooglePay: boolean;
}

const ExpressCheckoutButton = ({
  stripe,
  onElementReady,
  onElementError,
  allowApplePay,
  allowGooglePay,
}: ExpressCheckoutButtonProps) => {
  const context = useContext(CheckoutContext);
  const {
    actions: { runPayment },
    paymentMethodsAvailable,
    customer,
    featureFlags,
    sessionStartedWithoutValidTax,
    pricing,
    publisher,
    boot,
  } = context;

  const elements = useElements();
  useSyncStripeElementsAmount(elements, pricing.priceBreakdown.priceTotal);
  const {
    onShippingAddressChange: handleShippingAddressChange,
    capturedWalletTaxRef,
    applyWalletTaxToCheckout,
    logConfirmTaxState,
  } = useStripeWalletShippingZipTax(elements);
  const [isElementReady, setIsElementReady] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Country + FF only — email must not recreate ECE options (remount → wallets vanish).
  const taxCountry = customer.taxCountry ?? customer.geoLocator?.countryCode2 ?? '';
  // ACDEV-26742: display-only company name on the wallet sheet, gated by
  // APPLE_PAY_COMPANY_NAME_LABEL inside the builder. The express row is ONE
  // Stripe iframe for both wallets, so the branding necessarily applies to
  // the Google Pay sheet too (Stripe's `business.name` is wallet-agnostic).
  const companyName = resolveCheckoutGameName(boot?.gameName, publisher?.companyName || boot?.companyName);
  const expressCheckoutOptions = useMemo(() => {
    const hasApplePay =
      allowApplePay && isStripeExpressPaymentMethodExists(paymentMethodsAvailable, PaymentMethodEnum.APPLE_PAY);
    const hasGooglePay =
      allowGooglePay && isStripeExpressPaymentMethodExists(paymentMethodsAvailable, PaymentMethodEnum.GOOGLE_PAY);

    return {
      paymentMethods: {
        applePay: hasApplePay ? ('always' as const) : ('never' as const),
        googlePay: hasGooglePay ? ('always' as const) : ('never' as const),
        link: 'never' as const,
      },
      // `emailRequired` must live on the element options (not the click
      // `resolve()`) — Stripe rejects collection params passed on click.
      emailRequired: true,
      // ACDEV-25067 + ACDEV-27914: `shippingAddressRequired` (with its
      // mandatory `shippingRates` companion) is opt-in per-customer AND
      // FF-gated (25067) OR unresolved-boot-tax gated (27914) — only US/CA
      // customers whose session needs a wallet ZIP for tax get it; every
      // other jurisdiction OR FF-OFF session with a resolved boot tax gets
      // no shipping section in the sheet at all.
      ...getStripeExpressCheckoutShippingConfig(customer, featureFlags, sessionStartedWithoutValidTax),
      ...getStripeExpressCheckoutBusinessConfig(companyName, featureFlags),
      layout: {
        overflow: 'never' as const,
        maxColumns: 1 as const,
      },
      // Pin the real ECE wallet buttons to the same height as the branded
      // placeholders so the provider click targets line up underneath the
      // visible placeholder buttons.
      buttonHeight: 48,
      paymentMethodOrder: [APPLE_PAY_WALLET, GOOGLE_PAY_WALLET],
    } as StripeExpressCheckoutElementOptions;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- customer email must not remount ECE
  }, [
    paymentMethodsAvailable,
    allowApplePay,
    allowGooglePay,
    taxCountry,
    featureFlags,
    sessionStartedWithoutValidTax,
    companyName,
  ]);

  const handleReady = (e: StripeExpressCheckoutElementReadyEvent) => {
    setIsElementReady(true);
    onElementReady(e);
  };

  const handleLoadError = () => {
    logError(ErrorLogs.expressCheckoutError, getLogInfoData(context));
    setIsElementReady(false);
    onElementError();
  };

  const handleConfirm = async (event: StripeExpressCheckoutElementConfirmEvent) => {
    try {
      logConfirmTaxState(event);
      setIsProcessing(true);
      const paymentMethod =
        event.expressPaymentType === 'apple_pay' ? PaymentMethodEnum.APPLE_PAY : PaymentMethodEnum.GOOGLE_PAY;
      notifyPaymentIntentClick(context, { paymentMethodName: paymentMethod });

      const result = await runPayment(
        { event, elements: elements!, stripe, zipCode: capturedWalletTaxRef.current?.zipCode },
        paymentMethod
      );
      // `runPayment` surfaces its own errors and resolves `undefined`; the
      // wallet sheet still needs to hear about the failure or it spins until
      // Apple's / Google's timeout.
      if (isExpressPaymentFailure(result)) {
        failExpressCheckoutSheet(event);
      }
    } catch (error) {
      failExpressCheckoutSheet(event);
      applyWalletTaxToCheckout();
      logError('PayButtonByDevice - Payment error', error);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 relative">
      {isProcessing && (
        <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-50 rounded-xl">
          <Loader />
        </div>
      )}
      <div className="express-checkout-vertical">
        <ExpressCheckoutElement
          onClick={(event) => {
            if (!isElementReady) {
              setIsProcessing(true);
            }
            const paymentMethod =
              event.expressPaymentType === 'apple_pay' ? PaymentMethodEnum.APPLE_PAY : PaymentMethodEnum.GOOGLE_PAY;
            trackAndNotifyPaymentClick(context, { paymentMethodName: paymentMethod });
            // `emailRequired` is configured on the element options
            // (expressCheckoutOptions); Stripe rejects passing it on click.
            event.resolve();
          }}
          onShippingAddressChange={handleShippingAddressChange}
          onConfirm={handleConfirm}
          onCancel={applyWalletTaxToCheckout}
          onReady={handleReady}
          onLoadError={handleLoadError}
          options={expressCheckoutOptions}
        />
      </div>
    </div>
  );
};

/**
 * Stripe Express Checkout wrapper. Renders Apple+Google placeholders
 * from t=0 (whichever are configured) under per-button loader
 * overlays; swaps to the real Stripe-rendered Express Checkout
 * Element once Stripe.js boots AND the ECE reports `onReady` with
 * at least one available payment method.
 *
 * Why a custom multi-slot layout instead of the shared
 * `WalletButtonShell`: Stripe's `ExpressCheckoutElement` paints both
 * Apple Pay and Google Pay inside a single iframe — we can't split
 * it across two `WalletButtonShell`s. But a single shell only has
 * ONE overlay/spinner shared across both placeholders, which makes
 * the loading state read as one giant button rather than two
 * separate wallets. The `.wallet-pair` layout below keeps the
 * single-iframe constraint while giving Apple Pay and Google Pay
 * each their own loading overlay + spinner.
 *
 * Tri-state at the wrapper level:
 *   - `loading`     Stripe still booting OR ECE not yet ready —
 *                   placeholder layer visible, per-slot spinners
 *                   block clicks.
 *   - `ready`       ECE reported >=1 available method; placeholder
 *                   layer fades out, real ECE layer fades in with
 *                   the shared pulse animation.
 *   - `unavailable` ECE finished but reported zero available methods
 *                   for this device (e.g. desktop Safari without
 *                   Touch ID). Entire wrapper smoothly collapses.
 *
 * Per-slot collapse (Apple OR Google specifically unavailable):
 *   When ECE's `availablePaymentMethods` reports only a subset (e.g.
 *   Apple Pay rejected by Stripe's merchant validation but Google
 *   Pay accepted), the unavailable slot's `.wallet-collapse` wrapper
 *   animates to height 0 + opacity 0, and the surviving slot loses
 *   its sibling gap. Without this, the placeholder layer would
 *   dominate the grid row height (~104px for two stacked buttons)
 *   and leave a phantom ~56px gap above the lone real button.
 */
export const PayButtonByDevice = (props: ExpressCheckoutComponentProps) => {
  const context = useContext(CheckoutContext);
  const { paymentMethodsAvailable, template } = context;
  const reportWalletAvailability = useExpressWalletFallback();
  const tabFallbackGateOpen = useApplePayTabFallbackGate();

  // Stripe's ECE can never surface Apple Pay here — either an iOS <= 16
  // cross-origin iframe (no `ApplePaySession` before Safari 17) or Apple
  // already rejected this page's merchant domain. Render the new-tab
  // fallback for the Apple slot instead. On every gated device the row is
  // native-wallet-restricted (phone), so Google Pay does not render
  // alongside; if it ever is configured, the ECE below keeps painting it
  // with the Apple slot suppressed.
  const applePayFallbackActive =
    tabFallbackGateOpen && isStripeExpressPaymentMethodExists(paymentMethodsAvailable, PaymentMethodEnum.APPLE_PAY);

  if (!applePayFallbackActive) {
    return <StripeExpressCheckoutWallets {...props} />;
  }

  const handleFallbackReady = (isAvailable: boolean) => {
    reportWalletAvailability(isAvailable, {
      wallet: 'apple_pay',
      provider: ProviderEnum.STRIPE,
      reason: isAvailable ? undefined : 'unavailable',
    });
    props.onReady?.(isAvailable, undefined, isAvailable ? [PaymentMethodEnum.APPLE_PAY] : []);
  };

  const restrictToNativeWallet = shouldRestrictExpressToNativeWallet(template);
  const allowGooglePay =
    !restrictToNativeWallet || expressMethodForDevice(getDeviceType()) === PaymentMethodEnum.GOOGLE_PAY;
  const hasGooglePayConfigured =
    allowGooglePay && isStripeExpressPaymentMethodExists(paymentMethodsAvailable, PaymentMethodEnum.GOOGLE_PAY);

  if (!hasGooglePayConfigured) {
    return <ApplePayTabFallback onReady={handleFallbackReady} />;
  }
  return (
    <div className="flex flex-col gap-2">
      <ApplePayTabFallback onReady={handleFallbackReady} />
      <StripeExpressCheckoutWallets {...props} suppressApplePay />
    </div>
  );
};

const StripeExpressCheckoutWallets = ({
  onReady,
  suppressApplePay = false,
}: ExpressCheckoutComponentProps & { suppressApplePay?: boolean }) => {
  const context = useContext(CheckoutContext);
  const { pricing, paymentMethodsAvailable, template, featureFlags } = context;
  const reportWalletAvailability = useExpressWalletFallback();
  const elementsOptions = useStableStripeExpressElementsOptions(
    pricing.priceBreakdown.priceTotal,
    pricing.currencyCode
  );
  /**
   * Phone DEFAULT shows ONLY the device-native wallet: Apple Pay on iOS,
   * Google Pay on Android. We suppress the non-native wallet so Stripe
   * never paints (and never flashes a placeholder for) e.g. Google Pay
   * on iPhone. Desktop/tablet keeps both wallets.
   */
  const restrictToNativeWallet = shouldRestrictExpressToNativeWallet(template);
  const nativeMethod = restrictToNativeWallet ? expressMethodForDevice(getDeviceType()) : undefined;
  const allowApplePay = (!restrictToNativeWallet || nativeMethod === PaymentMethodEnum.APPLE_PAY) && !suppressApplePay;
  const allowGooglePay = !restrictToNativeWallet || nativeMethod === PaymentMethodEnum.GOOGLE_PAY;
  const [state, setState] = useState<WalletReadyState>('loading');
  /**
   * Per-method availability from ECE's `onReady`. `null` while still
   * loading (both placeholders shown). Once set, drives per-slot
   * collapse independent of the overall wrapper state.
   */
  const [availableMethods, setAvailableMethods] = useState<AvailablePaymentMethods | null>(null);

  const hasGooglePayConfigured =
    allowGooglePay && isStripeExpressPaymentMethodExists(paymentMethodsAvailable, PaymentMethodEnum.GOOGLE_PAY);
  const hasApplePayConfigured =
    allowApplePay && isStripeExpressPaymentMethodExists(paymentMethodsAvailable, PaymentMethodEnum.APPLE_PAY);

  // Slow-load stopwatch for the Apple Pay slot (only meaningful when Apple Pay
  // is configured here). `stripe_load` = mount → Stripe.js ready; `ece_probe`
  // = Stripe ready → ECE onReady/onLoadError.
  const mountTimeRef = useRef(Date.now());
  const stripeReadyAtRef = useRef<number | null>(null);
  const stripePhases = (now: number): ApplePayLoadPhases => {
    const stripeReadyAt = stripeReadyAtRef.current ?? now;
    return { stripe_load: stripeReadyAt - mountTimeRef.current, ece_probe: now - stripeReadyAt };
  };
  const trackAppleSlowLoad = (outcome: 'ready' | 'unavailable' | 'error') => {
    if (!hasApplePayConfigured) return;
    const now = Date.now();
    trackApplePaySlowLoad(context, {
      payment_provider: ProviderEnum.STRIPE,
      phases: stripePhases(now),
      total_ms: now - mountTimeRef.current,
      outcome,
    });
  };

  const reportReady = (isAvailable: boolean, methods?: AvailablePaymentMethods) => {
    // No per-wallet meta here: this reports the COMBINED availability, and the
    // Apple/Google-specific RUM logs are emitted separately via
    // `trackWalletAvailability` (the ECE reports each wallet individually).
    reportWalletAvailability(isAvailable);
    setState(isAvailable ? 'ready' : 'unavailable');
    onReady?.(isAvailable, undefined, getStripeExpressAvailableMethods(methods));
  };

  // Per-wallet Apple Pay / Google Pay availability → Coralogix RUM, so a wallet
  // that the player saw a placeholder for and then watched collapse (or that
  // never painted at all) is queryable per wallet — not hidden behind the
  // OR-combined signal `reportReady` sends.
  const trackWalletAvailability = (
    wallet: ExpressWalletName,
    configured: boolean,
    isAvailable: boolean,
    reason: string,
    timeoutMs?: number
  ) => {
    if (!configured) return;
    // `reportReady` sends the OR-combined signal with no per-wallet meta, so
    // the fallback tab's dead-end latch can only be set from here. No-op
    // outside that tab, and unreachable from the hand-off path below — arming
    // never succeeds in a top-level window.
    if (wallet === 'apple_pay' && !isAvailable) markApplePayTabUnusable();
    trackExpressWalletAvailability(context, {
      wallet,
      provider: ProviderEnum.STRIPE,
      is_available: isAvailable,
      reason: isAvailable ? undefined : reason,
      load_time_ms: Date.now() - mountTimeRef.current,
      timeout_ms: timeoutMs,
    });
  };

  const handleElementReady = (e: StripeExpressCheckoutElementReadyEvent) => {
    const methods = e.availablePaymentMethods ?? ({} as AvailablePaymentMethods);
    // Parent domain not registered for Apple Pay: ECE leaves applePay false
    // before any click. Arm the new-tab fallback so the fake button still
    // paints — PayButtonByDevice remounts with ApplePayTabFallback.
    if (hasApplePayConfigured && !methods.applePay && armApplePayTabFallbackIfEligible(featureFlags)) {
      trackAppleSlowLoad('unavailable');
      trackWalletAvailability('apple_pay', hasApplePayConfigured, false, 'domain_unsupported');
      return;
    }
    trackAppleSlowLoad(methods.applePay ? 'ready' : 'unavailable');
    setAvailableMethods(methods);
    trackWalletAvailability('apple_pay', hasApplePayConfigured, !!methods.applePay, 'device_unsupported');
    trackWalletAvailability('google_pay', hasGooglePayConfigured, !!methods.googlePay, 'device_unsupported');
    reportReady(Object.values(methods).some(Boolean), methods);
  };

  const handleElementError = () => {
    // Treat load-error the same as "none available": all configured
    // slots collapse, and the wrapper collapses too.
    if (handOffApplePayToTabFallback('load_error')) return;
    trackAppleSlowLoad('error');
    setAvailableMethods({} as AvailablePaymentMethods);
    trackWalletAvailability('apple_pay', hasApplePayConfigured, false, 'load_error');
    trackWalletAvailability('google_pay', hasGooglePayConfigured, false, 'load_error');
    reportReady(false);
  };

  const markAllUnavailable = (reason: 'init_error' | typeof EXPRESS_WALLET_ECE_HANG_TIMEOUT_REASON) => {
    const timeoutMs =
      reason === EXPRESS_WALLET_ECE_HANG_TIMEOUT_REASON ? EXPRESS_WALLET_ECE_HANG_TIMEOUT_MS : undefined;
    if (handOffApplePayToTabFallback(reason, timeoutMs)) return;
    trackAppleSlowLoad('error');
    setAvailableMethods({} as AvailablePaymentMethods);
    trackWalletAvailability('apple_pay', hasApplePayConfigured, false, reason, timeoutMs);
    trackWalletAvailability('google_pay', hasGooglePayConfigured, false, reason, timeoutMs);
    reportReady(false);
  };

  /**
   * Apple Pay can't come from THIS element (dead Stripe.js, load error, or
   * silence), but the new-tab fallback still can — the fake button pays
   * through a top-level tab on our own verified domain. So arm it and leave
   * the row alone instead of collapsing: the parent remounts with
   * `ApplePayTabFallback`, and Google Pay (when configured) reports again from
   * the fresh ECE that mounts beside it with the Apple slot suppressed. Same
   * hand-off `handleElementReady` does for an `applePay: false` probe; these
   * paths need it too now that framed iOS 17 reaches the real element first.
   *
   * Returns whether the fallback took over, in which case the caller must NOT
   * collapse the row.
   */
  const handOffApplePayToTabFallback = (reason: string, timeoutMs?: number): boolean => {
    if (!hasApplePayConfigured) return false;
    if (!armApplePayTabFallbackIfEligible(featureFlags)) return false;
    trackAppleSlowLoad('error');
    trackWalletAvailability('apple_pay', hasApplePayConfigured, false, reason, timeoutMs);
    return true;
  };

  const shouldShowExpressCheckout = hasGooglePayConfigured || hasApplePayConfigured;

  const { stripe, isLoading: isStripeLoading } = useStripeInitialize({
    paymentMethod: hasGooglePayConfigured ? PaymentMethodEnum.GOOGLE_PAY : PaymentMethodEnum.APPLE_PAY,
    isExpress: true,
  });

  useEffect(() => {
    if (stripe && !isStripeLoading && stripeReadyAtRef.current === null) {
      stripeReadyAtRef.current = Date.now();
    }
  }, [stripe, isStripeLoading]);

  // Stripe.js terminal failure: ECE never mounts → collapse both slots.
  // Only when we actually intended to show a wallet (Bugbot: avoid reporting
  // unavailable when native-wallet filtering left zero configured slots).
  useEffect(() => {
    if (!shouldShowExpressCheckout) return;
    if (isStripeLoading || stripe) return;
    markAllUnavailable('init_error');
  }, [shouldShowExpressCheckout, isStripeLoading, stripe]);

  // ECE mounted but silent — OUR client hang-breaker after Stripe.js is ready
  // (Stripe does not emit a timeout for a silent ECE).
  useExpressReadyTimeout(
    shouldShowExpressCheckout && state === 'loading' && Boolean(stripe) && !isStripeLoading,
    () => markAllUnavailable(EXPRESS_WALLET_ECE_HANG_TIMEOUT_REASON),
    EXPRESS_WALLET_ECE_HANG_TIMEOUT_MS
  );

  if (!shouldShowExpressCheckout) {
    return null;
  }

  /**
   * A slot is collapsed only AFTER ECE has reported (so we don't
   * pre-emptively hide a placeholder during the loading phase). Once
   * the report arrives, slot collapse mirrors what ECE actually
   * rendered: configured-but-not-available wallets disappear.
   */
  const isAppleSlotCollapsed = availableMethods !== null && !availableMethods.applePay;
  const isGoogleSlotCollapsed = availableMethods !== null && !availableMethods.googlePay;

  const isReady = state === 'ready';
  const isUnavailable = state === 'unavailable';

  return (
    <div
      className={getWalletPairClass(state)}
      data-testid="wallet-btn-stripe-express"
      data-wallet-state={state}
      aria-busy={state === 'loading' || undefined}
      aria-hidden={isUnavailable || undefined}
    >
      <div className="wallet-pair__layer wallet-pair__placeholders">
        {hasApplePayConfigured && (
          <WalletSlot
            brand="apple"
            placeholder={<ApplePayButtonPlaceholder />}
            isCollapsed={isAppleSlotCollapsed}
            isReady={isReady}
            loadingLabel="Loading Apple Pay"
          />
        )}
        {hasGooglePayConfigured && (
          <WalletSlot
            brand="google"
            placeholder={<GooglePayButtonPlaceholder />}
            isCollapsed={isGoogleSlotCollapsed}
            isReady={isReady}
            loadingLabel="Loading Google Pay"
          />
        )}
      </div>
      <div className="wallet-pair__layer wallet-pair__real">
        {stripe && !isStripeLoading && (
          <Elements stripe={stripe} options={elementsOptions}>
            <ExpressCheckoutButton
              stripe={stripe}
              onElementReady={handleElementReady}
              onElementError={handleElementError}
              allowApplePay={allowApplePay}
              allowGooglePay={allowGooglePay}
            />
          </Elements>
        )}
      </div>
    </div>
  );
};
